/**
 * BUNDLE QUANTITY SELECTOR (Homestead)
 * Portado desde v9.3.0 (assets/component-bundle.js) y adaptado a Homestead.
 *
 * Controla la fila de un bundle en el cart drawer y en la página del carrito:
 *  - "+" / "−": cambian la cantidad de TODAS las líneas del bundle a la vez.
 *  - Papelera: elimina todas las líneas del bundle.
 *
 * Usa atributos propios (data-bundle-action) y no lleva name="updates[]" ni data-index,
 * para que cart-items.js / quantity-input del tema no intercepten estos controles.
 * Después de actualizar refresca con los métodos nativos de Homestead.
 */

if (!customElements.get('bundle-quantity-selector')) {
  class BundleQuantitySelector extends HTMLElement {
    connectedCallback() {
      // Evita listeners duplicados si el nodo se reconecta
      if (this._initialized) return;
      this._initialized = true;

      this._keys = (this.dataset.bundleKeys || '')
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      this._quantity = parseInt(this.dataset.quantity, 10) || 1;

      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-bundle-action]');
      if (!btn || !this.contains(btn) || btn.disabled) return;
      evt.preventDefault();
      evt.stopPropagation();

      const action = btn.dataset.bundleAction;
      if (action === 'increase') this.update(this._quantity + 1);
      if (action === 'decrease') this.update(Math.max(this._quantity - 1, 1));
      if (action === 'remove') this.update(0);
    }

    async update(quantity) {
      if (this._busy || !this._keys.length) return;
      if (quantity === this._quantity && quantity !== 0) return;
      this._busy = true;

      const row = this.closest('.cart-item--bundle');
      const errorEl = row ? row.querySelector('.bundle-error') : null;
      if (row) row.classList.add('is-loading');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

      try {
        const updates = {};
        this._keys.forEach((key) => { updates[key] = quantity; });

        const res = await fetch(`${theme.routes.cart || '/cart'}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.description || data.message || theme.strings.cartError);
        }

        // Si se pidió más de lo disponible, Shopify ajusta la cantidad: avisamos
        if (quantity > 0) {
          const line = (data.items || []).find((i) => this._keys.includes(i.key));
          if (line && line.quantity < quantity && errorEl) {
            errorEl.textContent = 'No hay más stock disponible para este bundle.';
            errorEl.hidden = false;
          }
        }

        // Avisar a otros scripts (ej. aviso de preventa en autofill-user-data) que el carrito cambió
        document.dispatchEvent(new CustomEvent('on:bundle:change', {
          detail: { bundleCode: this.dataset.bundleCode, quantity, cart: data }
        }));

        this.refreshCart(data);
      } catch (error) {
        console.error('[Bundle]', error); // eslint-disable-line
        if (errorEl) {
          errorEl.textContent = error.message || 'No se pudo actualizar el bundle. Intenta nuevamente.';
          errorEl.hidden = false;
        }
        if (row) row.classList.remove('is-loading');
        this._busy = false;
      }
    }

    refreshCart(cart) {
      const inDrawer = !!this.closest('#cart-drawer');
      const cartDrawer = inDrawer ? document.getElementById('cart-drawer') : null;
      const cartItems = this.closest('cart-items');
      const isEmpty = !cart || cart.item_count === 0;

      // Página del carrito: si quedó vacío, recargar para mostrar el estado vacío completo
      if (!inDrawer && isEmpty) {
        window.location.reload();
        return;
      }

      // Cart drawer vacío: re-render completo del drawer
      if (cartDrawer && isEmpty && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh(true);
        return;
      }

      if (cartItems && typeof cartItems.refresh === 'function') {
        cartItems.refresh();
      } else if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh(true);
      } else {
        window.location.reload();
      }
    }
  }

  customElements.define('bundle-quantity-selector', BundleQuantitySelector);
}
